import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { PATIENT_PORT } from '@service/preclinical/patient.port';
import { MASTER_DATA_PORT } from '@service/preclinical/master-data.port';
import { PatientSearchResult } from '@model/patient/patient.model';
import { Site, ExamType, Region } from '@model/filter/filter-options.model';
import {
  ManualEntryPayload,
  ManualEntryResponse,
  ReprocessPayload,
  EntryByCodeResponse
} from '@model/patient/sample-entry.model';

@Injectable({
  providedIn: 'root'
})
export class AddSamplePresenter {
  private readonly patientPort = inject(PATIENT_PORT);
  private readonly masterDataPort = inject(MASTER_DATA_PORT);

  searchPatientById(
    identifierType: string,
    idNumber: string
  ): Observable<PatientSearchResult> {
    return this.patientPort.searchPatient({ identifierType, idNumber });
  }

  searchPatientByCode(specimenCode: string): Observable<PatientSearchResult> {
    return this.patientPort.searchByCode(specimenCode);
  }

  saveSample(
    caseNumber: string,
    sampleData: any
  ): Observable<EntryByCodeResponse> {
    return this.patientPort.saveSampleByCode(caseNumber, sampleData);
  }

  updateSample(
    caseNumber: string,
    sampleData: ManualEntryPayload
  ): Observable<any> {
    return this.patientPort.updateSample(caseNumber, sampleData);
  }

  saveReprocess(
    specimenCode: string,
    payload: ReprocessPayload
  ): Observable<void> {
    return this.patientPort.saveReprocess(specimenCode, payload);
  }

  saveManualSample(
    payload: ManualEntryPayload
  ): Observable<ManualEntryResponse> {
    return this.patientPort.saveManualSample(payload);
  }

  loadAllMasterData(): Observable<{
    specimenTypes: { id: string; name: string }[];
    sites: Site[];
    examTypes: ExamType[];
    regions: Region[];
    diagnoses: { id: string; name: string }[];
    specialists: { id: string; name: string }[];
    priorities: { id: string; name: string }[];
    laboratories: { id: string; name: string }[];
    reprocessCauses: { id: string; name: string }[];
    reprocessStatuses: { id: string; name: string }[];
  }> {
    return forkJoin({
      specimenTypes: this.patientPort.getSpecimenTypes(),
      sites: this.masterDataPort.getSites(),
      examTypes: this.masterDataPort.getExamTypes(),
      regions: this.masterDataPort.getRegions(),
      diagnoses: this.masterDataPort.getDiagnoses(),
      specialists: this.masterDataPort.getSpecialists(),
      priorities: this.masterDataPort.getPriorities(),
      laboratories: this.masterDataPort.getLaboratories(),
      reprocessCauses: this.masterDataPort.getReprocessCauses(),
      reprocessStatuses: this.masterDataPort.getReprocessStatuses()
    });
  }

  loadSpecimenTypes(): Observable<{ id: string; name: string }[]> {
    return this.patientPort.getSpecimenTypes();
  }

  loadSites(): Observable<Site[]> {
    return this.masterDataPort.getSites();
  }

  loadExamTypes(): Observable<ExamType[]> {
    return this.masterDataPort.getExamTypes();
  }

  loadRegions(): Observable<Region[]> {
    return this.masterDataPort.getRegions();
  }

  loadDiagnoses(): Observable<{ id: string; name: string }[]> {
    return this.masterDataPort.getDiagnoses();
  }

  loadSpecialists(): Observable<{ id: string; name: string }[]> {
    return this.masterDataPort.getSpecialists();
  }

  loadPriorities(): Observable<{ id: string; name: string }[]> {
    return this.masterDataPort.getPriorities();
  }

  loadLaboratories(): Observable<{ id: string; name: string }[]> {
    return this.masterDataPort.getLaboratories();
  }

  loadReprocessCauses(): Observable<{ id: string; name: string }[]> {
    return this.masterDataPort.getReprocessCauses();
  }

  loadReprocessStatuses(): Observable<{ id: string; name: string }[]> {
    return this.masterDataPort.getReprocessStatuses();
  }

  loadSpecimenContainers(): Observable<{ id: string; name: string }[]> {
    return this.masterDataPort.getSpecimenContainers();
  }

  loadProviders(): Observable<{ id: string; name: string }[]> {
    return this.masterDataPort.getProviders();
  }

  loadInsuranceCompanies(): Observable<{ id: string; name: string }[]> {
    return this.masterDataPort.getInsuranceCompanies();
  }
}
